import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { TaskListSectionModel } from '@models/taskListSection'

import type { TaskListSectionModuleState, EntityOptions } from '@redux/modules/types'

const watchEntityKeys = ['taskListSections']

interface useEntitiesOptions {
  performHttpRequests?: boolean,
  filters: {
    id?: number,
    task_list_id?: number,
  },
  requestOptions?: EntityOptions,
}

type useEntitiesPayload = {
  callbacks: {
    loadMore: () => void,
  },
  canLoadMore: boolean,
  filteredTaskListSections: TaskListSectionModel[],
  hasFilteredTaskListSections: boolean,
  loading: boolean,
}

function useTaskListSections(options: useEntitiesOptions): useEntitiesPayload {
  const { filters } = options || {}
  const {
    id: filterId,
    task_list_id: filterTaskListId,
  } = filters

  const {
    updatedEntities: { taskListSections: taskListSectionsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { taskListSections } = useSelector(reduxState => reduxState.entities)

  const filteredTaskListSections = useMemo((): TaskListSectionModel[] => {
    const filtered = Object.values(taskListSections).filter((taskListSection: TaskListSectionModel) => {
      const { id, task_list_id } = taskListSection

      const matchId = matchFilterNumber(id, filterId)
      const matchTaskList = matchFilterNumber(task_list_id, filterTaskListId)

      return (
        matchId
        && matchTaskList
      )
    })

    return sortArrayBy(filtered, 'asc', 'sort')
  }, [taskListSectionsUpdatedAt, JSON.stringify(filters)])

  const filteredTaskListSectionsCount = filteredTaskListSections.length

  const loadMorePayload = useLoadMore({
    loadedCount: filteredTaskListSectionsCount,
    filters,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  useReduxAction(
    'taskListSections',
    'loadTaskListSections',
    {
      ...filtersWithOffset,
      limit,
    },
    [performHttpRequests],
    {
      // @ts-expect-error
      shouldPerformFn: (entityReducer: TaskListSectionModuleState) => {
        const { loading } = entityReducer
        return performHttpRequests && !loading
      },
    },
  )

  const { loading } = useSelector(reduxState => reduxState.taskListSections)

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTaskListSections,
    hasFilteredTaskListSections: !!filteredTaskListSections.length,
    loading,
  }
}

export default useTaskListSections
